import { useEffect, useState } from 'react';

import type { GroupMember } from './groupsApi.ts';

/**
 * Tracks whether the light theme is active.
 *
 * Member colours are sent as a pair because no single hex is readable on both
 * backgrounds, so the renderer has to know which theme is showing. This follows the OS
 * setting, matching the `prefers-color-scheme` rules in global.css.
 */
export function usePrefersLight(): boolean {
  const [prefersLight, setPrefersLight] = useState(
    () => window.matchMedia('(prefers-color-scheme: light)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (event: MediaQueryListEvent) => setPrefersLight(event.matches);

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return prefersLight;
}

/** The rendering of a member's colour appropriate to the active theme. */
export function useMemberColour(member: Pick<GroupMember, 'colourHex' | 'colourOnLight'>): string {
  const prefersLight = usePrefersLight();

  return prefersLight ? member.colourOnLight : member.colourHex;
}
